import axios, { AxiosInstance } from 'axios'
import { useAuth0 } from '@auth0/auth0-react'
import { useMemo } from 'react'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { useManagingCustomer } from '@/contexts/ResellerManagementContext'

export const useAxiosWithAuth = (): AxiosInstance => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0()
  const { impersonatedUser } = useImpersonation()
  const { managingCustomer } = useManagingCustomer()

  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
    })

    // Request interceptor to add token
    instance.interceptors.request.use(
      async (config) => {
        try {
          const token = await getAccessTokenSilently()
          config.headers = config.headers || {}
          if (token) {
            config.headers.Authorization = `Bearer ${token}`
          }
          if (impersonatedUser?.email) {
            config.headers['X-Impersonated-User'] = impersonatedUser.email
          }
          if (managingCustomer) {
            // TODO: Make this a shared constant somewhere.
            config.headers['X-Managing-Customer'] = managingCustomer.id
          }
          return config
        } catch (error) {
          console.error('Failed to get access token', error)
          return Promise.reject(error)
        }
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor to handle expired tokens
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        // Check if originalRequest exists
        if (!originalRequest) {
          return Promise.reject(error)
        }

        if (originalRequest._retry) {
          return Promise.reject(error)
        }

        const errorResponseData = error.response?.data

        if (
          error.response &&
          (error.response.status === 401 ||
            (error.response.status === 400 &&
              errorResponseData &&
              typeof errorResponseData === 'object' &&
              errorResponseData.message &&
              errorResponseData.message.includes('missing or malformed jwt')))
        ) {
          if (
            errorResponseData.message &&
            errorResponseData.message.includes('missing or malformed jwt')
          ) {
            console.error('Token is malformed')
            await loginWithRedirect()
            return Promise.reject(error)
          }

          originalRequest._retry = true

          try {
            // Attempt to get a new token
            const token = await getAccessTokenSilently({ cacheMode: 'off' })
            originalRequest.headers.Authorization = `Bearer ${token}`
            // Retry the original request with the new token
            return instance(originalRequest)
          } catch (tokenError) {
            console.error('Token refresh failed:', tokenError)
            // Redirect to login if unable to refresh token
            await loginWithRedirect()
            return Promise.reject(tokenError)
          }
        }

        return Promise.reject(error)
      }
    )

    return instance
  }, [
    getAccessTokenSilently,
    loginWithRedirect,
    impersonatedUser,
    managingCustomer,
  ])

  return axiosInstance
}
